import { animate } from "framer-motion"

// Il campo che cresce: aprendo l'editor, o un esercizio del quaderno, lo
// schema parte da dov'era (la copertina, il campo in alto) e arriva dove va,
// e il suo fondo si allarga con lui fino a coprire lo schermo. Chiudendo, il
// contrario. È il gesto del "FLIP": si misura dove sta la cosa da cui si
// parte e dove sta quella d'arrivo, si fa partire la seconda spostata e
// scalata sopra la prima, e la si lascia andare al suo posto.
//
// Il disegno ha la stessa forma dappertutto (un viewBox che sta intero nel
// suo riquadro), quindi basta una scala sola e non si deforma.

export const MOTION_EASE = [0.32, 0.72, 0, 1]

export const hasSize = rect => Boolean(rect) && rect.width > 0 && rect.height > 0

// Dove un viewBox `width`×`height` è disegnato davvero dentro un riquadro:
// intero e al centro, come fa l'SVG di suo
export const drawnRect = (box, width, height) => {
	const k = Math.min(box.width / width, box.height / height)
	const w = width * k
	const h = height * k
	return { left: box.left + (box.width - w) / 2, top: box.top + (box.height - h) / 2, width: w, height: h }
}

// Lo spostamento e la scala che portano `to` sopra `from`, centro su centro
export const flipFrom = (from, to) => ({
	x: from.left + from.width / 2 - (to.left + to.width / 2),
	y: from.top + from.height / 2 - (to.top + to.height / 2),
	scale: from.width / to.width,
})

export const transformOf = ({ x, y, scale }) => `translateX(${x}px) translateY(${y}px) scale(${scale})`

// Un ritaglio dello schermo grande come `rect`, con gli angoli tondi di
// `radius` (uno per tutti, o i quattro da quello in alto a sinistra in
// giro): sempre gli stessi otto numeri, perché l'animazione passi dall'uno
// all'altro numero per numero
export const insetOf = (rect, radius = 0) => {
	const top = Math.max(0, rect.top)
	const left = Math.max(0, rect.left)
	const right = Math.max(0, window.innerWidth - rect.left - rect.width)
	const bottom = Math.max(0, window.innerHeight - rect.top - rect.height)
	const corners = (Array.isArray(radius) ? radius : [radius, radius, radius, radius]).map(r => `${r}px`)
	return `inset(${top}px ${right}px ${bottom}px ${left}px round ${corners.join(" ")})`
}

export const FULL_SCREEN = "inset(0px 0px 0px 0px round 0px 0px 0px 0px)"

// Uno stato è, per ogni pezzo, i valori che ha: { campo: { x, y, scale,
// opacity }, fondo: { clipPath }, ... }. `x`, `y` e `scale` diventano un
// transform, il resto va com'è.
export const styleOf = ({ x, y, scale, ...rest }) =>
	x === undefined && y === undefined && scale === undefined
		? rest
		: { ...rest, transform: transformOf({ x: x ?? 0, y: y ?? 0, scale: scale ?? 1 }) }

// I passi da uno stato all'altro, pezzo per pezzo: `els` dice chi è ogni
// pezzo, `timing` i tempi di quelli che non vanno coi tempi di tutti.
// Aprendo (`enter`) il primo stato si scrive subito.
export const between = (els, from, to, { enter = false, timing = {} } = {}) =>
	Object.keys(from).map(name => ({
		el: els[name],
		// quello che non cambia non si anima: un'animazione da 1 a 1 non
		// finisce mai, e terrebbe ferma la chiusura
		keyframes: Object.fromEntries(
			Object.keys(from[name])
				.filter(key => from[name][key] !== to[name][key])
				.map(key => [key, [from[name][key], to[name][key]]]),
		),
		set: enter ? styleOf(from[name]) : null,
		...timing[name],
	}))

// Porta ogni elemento dai primi valori agli ultimi, tutti insieme; finisce
// quando ha finito l'ultimo. `set` si scrive subito, prima che il telefono
// disegni: senza, si vedrebbe un fotogramma della posizione d'arrivo.
// La fine la dice l'orologio e non le animazioni: le loro promesse, quando
// il telefono le fa da sé, a volte non arrivano.
export const play = (steps, { duration, delay = 0 }) => {
	const timed = steps
		.filter(step => step.el)
		.map(step => ({ ...step, duration: step.duration ?? duration, delay: step.delay ?? delay }))
	const running = timed.map(step => {
		if (step.set) Object.assign(step.el.style, step.set)
		if (Object.keys(step.keyframes).length === 0) return null
		return animate(step.el, step.keyframes, { duration: step.duration, delay: step.delay, ease: MOTION_EASE })
	})
	const total = Math.max(0, ...timed.map(step => step.delay + step.duration))
	return {
		finished: new Promise(resolve => setTimeout(resolve, total * 1000 + 20)),
		stop: () => running.forEach(animation => animation?.stop()),
	}
}
